import {adminBlogPostsQuery} from '@/queries/blog';
import {Button} from '@/components/ui/button';
import {Pill} from '@/components/ui/pill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {Typography} from '@/components/ui/typography';
import {formatDate} from '@/utils/displayFormatUtils';
import {getProfileName} from '@/utils/getProfileName';
import {deleteBlogPost, updateBlogPost} from '@/server/blog';
import {toastManager} from '@/components/ui/toast';
import {createFileRoute, Link, useRouter} from '@tanstack/react-router';
import {Ellipsis} from 'lucide-react';
import {useState} from 'react';
import {Menu, MenuTrigger, MenuPopup, MenuItem} from '@/components/ui/menu';
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const Route = createFileRoute('/admin/blog/')({
  component: AdminBlogListPage,
  loader: async () => {
    const posts = await adminBlogPostsQuery();
    return {posts};
  },
});

/** Badge colors and labels for blog post status. */
const statusBadgeConfig: Record<string, {color: string; label: string}> = {
  DRAFT: {color: '#ca8a04', label: 'Draft'},
  PUBLISHED: {color: '#16a34a', label: 'Published'},
};

/** Actions menu for a blog post row. */
function PostActionsMenu({
  post,
}: {
  post: {
    id: string;
    title: string;
    slug: string;
    status: string;
    heroImageUrl: string | null;
  };
}) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function handleStatusChange(status: 'DRAFT' | 'PUBLISHED') {
    setLoading(true);
    try {
      await updateBlogPost({data: {id: post.id, status}});
      router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Something went wrong',
        type: 'error',
      });
      setLoading(false);
    }
  }

  async function handleDelete() {
    setLoading(true);
    try {
      await deleteBlogPost({data: post.id});
      router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to delete post',
        type: 'error',
      });
      setLoading(false);
      setDeleteOpen(false);
    }
  }

  return (
    <>
      <Menu>
        <MenuTrigger disabled={loading}>
          <Button variant="ghost" size="icon" disabled={loading}>
            <Ellipsis className="w-4 h-4" />
          </Button>
        </MenuTrigger>
        <MenuPopup align="end">
          <Link to="/admin/blog-edit/$id" params={{id: post.id}}>
            <MenuItem>Edit</MenuItem>
          </Link>
          {post.status === 'DRAFT' ? (
            <Link to="/blog/$slug" params={{slug: post.slug}}>
              <MenuItem>Preview</MenuItem>
            </Link>
          ) : (
            <Link to="/blog/$slug" params={{slug: post.slug}}>
              <MenuItem>View</MenuItem>
            </Link>
          )}
          {post.status === 'DRAFT' ? (
            <MenuItem onClick={() => handleStatusChange('PUBLISHED')}>
              Publish
            </MenuItem>
          ) : (
            <MenuItem onClick={() => handleStatusChange('DRAFT')}>
              Unpublish
            </MenuItem>
          )}
          <MenuItem
            className="text-error-500 data-highlighted:text-error-500"
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </MenuItem>
        </MenuPopup>
      </Menu>
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete blog post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{post.title}
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={loading}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}

/** Admin page listing all blog posts in a table. */
function AdminBlogListPage() {
  const {posts} = Route.useLoaderData();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <Typography level="H3">All Posts</Typography>
        <Link to="/admin/blog-create">
          <Button>Create Post</Button>
        </Link>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-12">
          <Typography level="B1" className="text-gray-500">
            No blog posts yet.
          </Typography>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden lg:table-cell">Author</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((post) => {
                const badge = statusBadgeConfig[post.status];
                return (
                  <TableRow key={post.id} className="group/row">
                    <TableCell>
                      <Typography level="B2">{post.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Pill size="xs" color={badge.color} className="w-fit">
                        {badge.label}
                      </Pill>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Typography level="B3" className="text-gray-600">
                        {post.author
                          ? getProfileName(post.author, 'Wayra')
                          : 'Wayra'}
                      </Typography>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Typography level="B3" className="text-gray-600">
                        {formatDate(post.publishedAt ?? post.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="lg:opacity-0 lg:group-hover/row:opacity-100 transition-opacity">
                        <PostActionsMenu post={post} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
